export const SURVEY_CUSTOM_CSS_SCOPE_CLASS = 'survey-custom-css-scope';
export const SURVEY_CUSTOM_CSS_SCOPE_SELECTOR = `.${SURVEY_CUSTOM_CSS_SCOPE_CLASS}`;

const DROP_RULE_TYPES = new Set([
  CSSRule.IMPORT_RULE,
  CSSRule.CHARSET_RULE,
  CSSRule.NAMESPACE_RULE,
  CSSRule.PAGE_RULE,
]);

const ROOT_SELECTOR_PATTERN = /^(html|body|:root)$/i;

const stripImportRules = (customCss: string): string => customCss.replace(/@import[^;]*;/gi, '');

const splitSelectors = (selectorText: string): string[] => {
  const selectors: string[] = [];
  let currentSelector = '';
  let parenthesisDepth = 0;
  let bracketDepth = 0;
  let currentQuote: '"' | "'" | null = null;

  for (let index = 0; index < selectorText.length; index += 1) {
    const character = selectorText[index];
    const previousCharacter = selectorText[index - 1];

    if (currentQuote) {
      currentSelector += character;

      if (character === currentQuote && previousCharacter !== '\\') {
        currentQuote = null;
      }

      continue;
    }

    if (character === '"' || character === "'") {
      currentQuote = character;
      currentSelector += character;
      continue;
    }

    if (character === '(') {
      parenthesisDepth += 1;
      currentSelector += character;
      continue;
    }

    if (character === ')') {
      parenthesisDepth = Math.max(parenthesisDepth - 1, 0);
      currentSelector += character;
      continue;
    }

    if (character === '[') {
      bracketDepth += 1;
      currentSelector += character;
      continue;
    }

    if (character === ']') {
      bracketDepth = Math.max(bracketDepth - 1, 0);
      currentSelector += character;
      continue;
    }

    if (character === ',' && parenthesisDepth === 0 && bracketDepth === 0) {
      if (currentSelector.trim()) {
        selectors.push(currentSelector.trim());
      }

      currentSelector = '';
      continue;
    }

    currentSelector += character;
  }

  if (currentSelector.trim()) {
    selectors.push(currentSelector.trim());
  }

  return selectors;
};

const consumeLeadingRootSelectorChain = (selector: string): string | null => {
  let remainingSelector = selector.trim();
  let consumedRoot = false;

  while (remainingSelector.length > 0) {
    const rootMatch = remainingSelector.match(/^(html|body|:root)(?=$|\s|[>+~])/i);

    if (!rootMatch) {
      break;
    }

    consumedRoot = true;
    remainingSelector = remainingSelector.slice(rootMatch[0].length).trimStart();

    const combinatorMatch = remainingSelector.match(/^[>+~]/);

    if (combinatorMatch) {
      remainingSelector = remainingSelector.slice(combinatorMatch[0].length).trimStart();
      continue;
    }

    if (remainingSelector.startsWith(' ')) {
      remainingSelector = remainingSelector.trimStart();
    }
  }

  if (!consumedRoot) {
    return null;
  }

  return remainingSelector;
};

export const scopeSurveyCustomCssSelector = (selector: string): string => {
  const trimmedSelector = selector.trim();

  if (!trimmedSelector) {
    return '';
  }

  if (ROOT_SELECTOR_PATTERN.test(trimmedSelector)) {
    return SURVEY_CUSTOM_CSS_SCOPE_SELECTOR;
  }

  const selectorWithoutRootChain = consumeLeadingRootSelectorChain(trimmedSelector);

  if (selectorWithoutRootChain !== null) {
    return selectorWithoutRootChain
      ? `${SURVEY_CUSTOM_CSS_SCOPE_SELECTOR} ${selectorWithoutRootChain}`
      : SURVEY_CUSTOM_CSS_SCOPE_SELECTOR;
  }

  return `${SURVEY_CUSTOM_CSS_SCOPE_SELECTOR} ${trimmedSelector}`;
};

const serializeStyleRule = (rule: CSSStyleRule): string => {
  const scopedSelectorText = splitSelectors(rule.selectorText)
    .map(scopeSurveyCustomCssSelector)
    .filter(Boolean)
    .join(', ');

  if (!scopedSelectorText || !rule.style.cssText) {
    return '';
  }

  return `${scopedSelectorText} { ${rule.style.cssText} }`;
};

const serializeGroupingRule = (ruleHeader: string, ruleList: CSSRuleList | undefined): string => {
  if (!ruleList || ruleList.length === 0) {
    return '';
  }

  const serializedChildren = Array.from(ruleList).map(serializeRule).filter(Boolean).join(' ');

  if (!serializedChildren) {
    return '';
  }

  return `${ruleHeader} { ${serializedChildren} }`;
};

const getGroupingRuleHeader = (rule: CSSRule): string => {
  const cssTextBeforeBlock = rule.cssText.split('{')[0]?.trim();

  return cssTextBeforeBlock ?? '';
};

const serializeRule = (rule: CSSRule): string => {
  if (DROP_RULE_TYPES.has(rule.type)) {
    return '';
  }

  if (rule.type === CSSRule.STYLE_RULE) {
    return serializeStyleRule(rule as CSSStyleRule);
  }

  if (rule.type === CSSRule.MEDIA_RULE) {
    return serializeGroupingRule(getGroupingRuleHeader(rule), (rule as CSSMediaRule).cssRules);
  }

  if (rule.type === CSSRule.SUPPORTS_RULE) {
    return serializeGroupingRule(getGroupingRuleHeader(rule), (rule as CSSSupportsRule).cssRules);
  }

  if (rule.type === CSSRule.FONT_FACE_RULE || rule.type === CSSRule.KEYFRAMES_RULE) {
    return rule.cssText;
  }

  return '';
};

const parseCssRules = (customCss: string): CSSRule[] => {
  if (typeof document === 'undefined') {
    return [];
  }

  const sanitizedCss = stripImportRules(customCss);

  if (!sanitizedCss.trim()) {
    return [];
  }

  const styleElement = document.createElement('style');
  styleElement.setAttribute('data-survey-custom-css-scope', 'true');
  styleElement.textContent = sanitizedCss;

  try {
    document.head.appendChild(styleElement);
    return Array.from(styleElement.sheet?.cssRules ?? []);
  } finally {
    styleElement.remove();
  }
};

export const scopeSurveyCustomCss = (customCss?: string | null): string => {
  if (!customCss?.trim()) {
    return '';
  }

  try {
    return parseCssRules(customCss).map(serializeRule).filter(Boolean).join(' ').trim();
  } catch (error) {
    console.error('Failed to scope survey custom CSS', error);
    return '';
  }
};
